import React from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import { Card, CardContent, CardActions, Box, makeStyles, Typography, IconButton } from "@material-ui/core";
import { useTranslate } from "react-polyglot";
import CloseIcon from "@material-ui/icons/Close";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import oneCoin from "../../assets/ic_one_coin.png";
import icCheck from "../../assets/check-circle.svg";
import { useBaseData } from "../../Providers/Data/BaseDataProvider.jsx";
import PrimaryDarkTypography from "../../SharedComponents/PrimaryDarkTypography.jsx";
import FlatGreyButton from "../../SharedComponents/FlatGreyButton.jsx";

const useStyles = makeStyles((theme) => ({
  cardRoot: {
    display: "flex",
    flexDirection: "column",
    minHeight: 350,
    alignItems: "center",
    paddingTop: 16,
  },
  content: {
    flex: "1 0 auto",
    padding: "18px 24px",
    maxHeight: "70vh",
    overflowY: "auto",
    minWidth: "80%",
  },
  contentBox: {
    "& p *": {
      overflowWrap: "anywhere",
    },
    overflowWrap: "anywhere",
    padding: "24px 0",
  },
  action: {
    padding: "18px 24px",
    marginLeft: "auto",
    marginRight: "auto",
  },
  closeButton: {
    position: "absolute",
    top: 8,
    right: 8,
  },
  accordionItem: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "15px 25px",
    backgroundColor: "#fff",
    border: "2px solid rgba(0, 0, 0, .08)",
    borderRadius: theme.spacing(1.5),
    marginTop: 8,
    "& .MuiSvgIcon-root": {
      fill: "#909293",
    },
  },
  learnedAccordionItem: {
    backgroundColor: "#c5ffc9",
  },

  accordionReward: {
    width: 25,
    height: 25,
  },
  contentTitle: {
    color: "#3d4853",
    fontSize: 14,
    fontWeight: 600,
  },
  learnedContentTitle: {
    color: "#003915",
  },
  contentText: {
    color: "#54606B",
    fontSize: 12,
  },
  coinBalance: {
    color: theme.palette.secondary.yellow,
    marginRight: theme.spacing(0.5),
  },
  closeButtonBottom: {
    minWidth: 200,
    textTransform: "none",
    borderRadius: 22,
  },
  itemsTaskBox: {
    backgroundColor: "#FAFBFB",
    border: "2px solid rgba(0, 0, 0, .08)",
    borderRadius: theme.spacing(1.5),
    padding: `${theme.spacing(0.5)}px ${theme.spacing(1.5)}px`,
  },
  itemTask: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: `${theme.spacing(1)}px 0`,
    borderBottom: "2px solid rgba(0, 0, 0, .08)",
    "&:last-child": {
      borderBottom: "none",
    },
    "& .MuiSvgIcon-root": {
      fill: "#909293",
    },
    cursor: "pointer",
  },
}));

/**
 * @param {?TheNews} task
 * @param {?Function} onClose
 * @returns {*}
 * @constructor
 */
const TaskDetails = ({ task, onClose }) => {
  const classes = useStyles();
  const t = useTranslate();
  const history = useHistory();
  const { branches } = useBaseData();

  if (!task) {
    return null;
  }
  /** */
  const findDeckByExerciseItemId = (branches, itemId) => {
    return branches.flatMap((branch) => branch.decks || []).find((deck) => deck.exercises?.items?.includes(itemId));
  };

  /** */
  const itemTaskClickHandler = (item) => {
    let url = "";
    if (task.data.decks) {
      url = `/learn/${item.id}`;
    } else if (task.data.exercises) {
      const deck = findDeckByExerciseItemId(branches, item.id);
      url = `/learn/${deck.id}`;
    } else if (task.data.homeworks_todo) {
      url = `/homeworks/todo/${item.id}`;
    } else if (task.data.events) {
      url = `/events/${item.id}`;
    }
    if (url) {
      history.push(url);
    }

    onClose && onClose();
  };

  /** */
  const onCloseCard = () => {
    onClose && onClose();
  };

  return (
    <Card className={classes.cardRoot}>
      <PrimaryDarkTypography variant="h6" gutterBottom>
        {t("missions.tasks.title")}
      </PrimaryDarkTypography>
      <CardContent className={classes.content}>
        <Box className={classes.accordionItem}>
          <div>
            <Typography className={`${classes.contentTitle} ${task.learned ? classes.learnedContentTitle : ""}`}>
              {task.name}
            </Typography>
            <Typography className={classes.contentText}>{task.data?.content}</Typography>
          </div>
          <Box display="flex" alignItems="center">
            <Typography className={classes.coinBalance}>{task.coins}</Typography>
            <img className={classes.accordionReward} src={oneCoin} alt="oneCoin" />
          </Box>
        </Box>
        <Typography className={classes.contentBox}>{task.description}</Typography>
        {task.data.items && (
          <Box className={classes.itemsTaskBox}>
            {task.data.items.map((item) => (
              <Box key={item.id} className={classes.itemTask} onClick={() => itemTaskClickHandler(item)}>
                <Typography className={`${classes.contentTitle} ${item.learned ? classes.learnedContentTitle : ""}`}>
                  {item.name}
                </Typography>
                <Box>
                  {item.learned && <img className={classes.learnedStatusIcon} src={icCheck} alt="" />}
                  <NavigateNextIcon />
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
      <CardActions className={classes.action}>
        <FlatGreyButton className={classes.closeButtonBottom} onClick={onCloseCard}>
          {t("closeButton")}
        </FlatGreyButton>
      </CardActions>
      <IconButton aria-label="close" className={classes.closeButton} onClick={onCloseCard}>
        <CloseIcon />
      </IconButton>
    </Card>
  );
};
TaskDetails.propTypes = {
  task: PropTypes.object.isRequired,
  onClose: PropTypes.func,
};

export default TaskDetails;
